/**
 * ProductFontSelector Component
 *
 * UI for selecting caption fonts in e-commerce video templates.
 * Shows all fonts with Pro fonts locked if user doesn't have pro_fonts feature.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Lock, Zap, Crown } from 'lucide-react';
import { CORE_FONTS, PRO_FONTS, ALL_FONTS, getFontById } from '../../constants/fonts';
import { useHasFeature } from '../../hooks/useSubscription';
import { showFeatureLockedModal } from '../../utils/subscriptionModals';

// Font button component
const FontButton = ({ font, isSelected, isLocked, onClick, onLockedClick }) => {
  const handleClick = () => {
    if (isLocked) {
      onLockedClick?.();
    } else {
      onClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`
        relative flex items-center gap-3 p-3 rounded-lg border-2 transition-all w-full text-left
        ${isSelected
          ? 'border-purple-500 bg-purple-50'
          : isLocked
            ? 'border-gray-200 bg-gray-50 opacity-75 cursor-not-allowed'
            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
        }
      `}
    >
      <div className={`p-2 rounded-lg ${isSelected ? 'bg-purple-100' : 'bg-gray-100'}`}>
        <span
          className="text-lg font-medium"
          style={{ fontFamily: `${font.family}, sans-serif` }}
        >
          Aa
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isSelected ? 'text-purple-900' : 'text-gray-900'}`}>
            {font.name}
          </span>
          {font.tier === 'pro' && (
            <Crown className="w-3.5 h-3.5 text-amber-500" />
          )}
          {isSelected && <Check className="w-4 h-4 text-purple-500" />}
          {isLocked && <Lock className="w-4 h-4 text-gray-400" />}
        </div>
        <p className="text-xs text-gray-500 truncate">{font.description}</p>
      </div>
    </button>
  );
};

/**
 * Main ProductFontSelector Component
 */
export const ProductFontSelector = ({
  captionFont,
  onCaptionFontChange,
  llmSuggested,
  accountId,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(!compact);

  // Check if user has access to pro fonts
  const { hasFeature: hasProFonts } = useHasFeature('pro_fonts', accountId);

  const selectedFont = getFontById(captionFont) || getFontById('Modern');

  const handleLockedFontClick = () => {
    showFeatureLockedModal('pro_fonts', t);
  };

  // Compact mode - just shows current selection with expand button
  if (compact && !isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="flex items-center justify-between w-full p-3 rounded-lg border border-gray-200 hover:border-purple-300 transition-all"
      >
        <div className="flex items-center gap-3">
          <span
            className="text-lg font-medium"
            style={{ fontFamily: `${selectedFont.family}, sans-serif` }}
          >
            Aa
          </span>
          <span className="font-medium text-gray-900">{selectedFont.name}</span>
        </div>
        <ChevronDown className="w-5 h-5 text-gray-400" />
      </button>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with collapse button (in compact mode) */}
      {compact && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {t('modal.product.captionFont', { defaultValue: 'Caption Font' })}
          </span>
          <button
            onClick={() => setIsExpanded(false)}
            className="text-xs text-purple-600 hover:text-purple-800"
          >
            {t('common.collapse', { defaultValue: 'Collapse' })}
          </button>
        </div>
      )}

      {/* Core Fonts Section */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          {t('modal.product.coreFonts', { defaultValue: 'Standard Fonts' })}
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {CORE_FONTS.map((font) => (
            <FontButton
              key={font.id}
              font={font}
              isSelected={captionFont === font.id}
              isLocked={false}
              onClick={() => onCaptionFontChange(font.id)}
            />
          ))}
        </div>
      </div>

      {/* Pro Fonts Section */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h4 className="text-sm font-medium text-gray-700">
            {t('modal.product.proFonts', { defaultValue: 'Pro Fonts' })}
          </h4>
          <Crown className="w-4 h-4 text-amber-500" />
          {!hasProFonts && (
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
              {t('modal.product.proRequired', { defaultValue: 'Pro required' })}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {PRO_FONTS.map((font) => (
            <FontButton
              key={font.id}
              font={font}
              isSelected={captionFont === font.id}
              isLocked={!hasProFonts}
              onClick={() => onCaptionFontChange(font.id)}
              onLockedClick={handleLockedFontClick}
            />
          ))}
        </div>
      </div>

      {/* LLM Suggestion indicator */}
      {llmSuggested?.captionFont && llmSuggested.captionFont !== captionFont && (
        <button
          onClick={() => {
            const suggestedFont = getFontById(llmSuggested.captionFont);
            if (suggestedFont?.tier === 'pro' && !hasProFonts) {
              handleLockedFontClick();
            } else {
              onCaptionFontChange(llmSuggested.captionFont);
            }
          }}
          className="flex items-center gap-2 text-xs text-purple-600 hover:text-purple-800 mt-2"
        >
          <Zap className="w-3 h-3" />
          {t('modal.product.useAiSuggestion', { defaultValue: 'Use AI suggestion' })}:
          {getFontById(llmSuggested.captionFont)?.name}
        </button>
      )}
    </div>
  );
};

export default ProductFontSelector;
